const keyBy = require('lodash/keyBy')
const { ProxifiedEntityApi } = require('../common')
const RmsAdapter = require('./data-access/adapter')
const StockTransformAdapter = require('./data-access/rms-stock-to-avocado')
const PurchaseOrdersSyncAdapter = require('./data-access/purchase-orders-sync-adapter')
const SalesOrdersSyncAdapter = require('./data-access/sales-orders-sync-adapter')
const { fsShipmentDateParser } = require('../shipment/tools/safe-date-parser')
const createBatchHelpers = require('./batches')
const { validateShipmentLine } = require('./tools/validate-shipment-line')

/**
 * Base API class for integration services
 * Provides common validation, error handling, and processing patterns
 */
class BaseIntegrationApi {
  constructor (pgConnection, logger, mainApi, user, integrationName) {
    this.pgConnection = pgConnection
    this.logger = logger
    this.mainApi = mainApi
    this.user = user
    this.integrationName = integrationName
  }

  /**
   * Logs operation start
   * @param {string} operation - Operation name
   * @param {Object} context - Additional context
   */
  logOperationStart (operation, context = {}) {
    this.logger.info(`${this.integrationName} ${operation} started`, {
      integration: this.integrationName,
      operation,
      ...context
    })
  }

  /**
   * Logs operation success
   * @param {string} operation - Operation name
   * @param {Object} result - Operation result
   * @param {Object} context - Additional context
   */
  logOperationSuccess (operation, result, context = {}) {
    this.logger.info(`${this.integrationName} ${operation} completed successfully`, {
      integration: this.integrationName,
      operation,
      result: typeof result === 'object' ? Object.keys(result) : result,
      ...context
    })
  }

  /**
   * Logs operation error
   * @param {string} operation - Operation name
   * @param {Error} error - Error object
   * @param {Object} context - Additional context
   */
  logOperationError (operation, error, context = {}) {
    this.logger.error(`${this.integrationName} ${operation} failed`, {
      integration: this.integrationName,
      operation,
      error: error.message,
      stack: error.stack,
      ...context
    })
  }
}

class RmsApi extends BaseIntegrationApi {
  constructor (pgConnection, logger, mainApi, user, rmsApiConfigs = {}, isomorphicFetch) {
    super(pgConnection, logger, mainApi, user, 'RMS')
    this.fetch = isomorphicFetch
    this.rmsAdapter = new RmsAdapter(rmsApiConfigs, this.fetch, pgConnection, logger, mainApi, user)
    this.stockTransformAdapter = new StockTransformAdapter(mainApi, pgConnection, logger, user.name)
    this.purchaseOrdersSyncAdapter = new PurchaseOrdersSyncAdapter({ pgConnection, logger, user })
    this.salesOrdersSyncAdapter = new SalesOrdersSyncAdapter({ pgConnection, logger, user })
    this.batchHelpers = createBatchHelpers({ mainApi, logger })
  }

  async shipmentsPost (payload) {
    // Log full payload for debugging
    if (payload) {
      const logPayload = {
        shipmentId: payload.shipmentId,
        orderId: payload.orderId,
        status: payload.status,
        origin: payload.origin,
        destination: payload.destination,
        expectedDeliveryDate: payload.expectedDeliveryDate,
        linesCount: payload.lines ? payload.lines.length : 0,
        lines: payload.lines ? payload.lines.map(line => ({
          productId: line.productId,
          batchNumber: line.batchNumber || '(missing)',
          manufacturer: line.manufacturer || '(missing)',
          quantity: line.quantity
        })) : []
      }
      this.logger.info('RMS shipmentsPost full payload', logPayload)
    }

    this.logOperationStart('shipmentsPost', { payloadKeys: Object.keys(payload || {}) })

    const { shipmentId, orderId, status, origin, destination, lines, expectedDeliveryDate, programId, funderId } = payload || {}
    const { SHIPMENT_STATUS } = require('../shipment/constants')

    if (!programId || !funderId) {
      throw new Error(`programId and funderId are required for shipmentId: ${shipmentId}`)
    }

    // Default to PACKED when not provided to support smoke tests with empty payloads
    const effectiveStatus = status || SHIPMENT_STATUS.PACKED
    if (effectiveStatus !== SHIPMENT_STATUS.PACKED) {
      throw new Error('Invalid status. Expected "packed"')
    }

    if (!origin || !destination) {
      throw new Error(`Origin and Destination codes are required for shipmentId: ${shipmentId}`)
    }

    const originCode = origin.id
    const destinationCode = destination.id

    if (!originCode || !destinationCode) {
      throw new Error(`Origin and Destination codes are required for shipmentId: ${shipmentId}`)
    }

    const locations = await this.mainApi.location.getByWarehouseCodes([originCode, destinationCode])
    const locationsByCode = keyBy(locations, 'additionalData.warehouseCode')

    const missingLocations = []
    if (!locationsByCode[originCode]) missingLocations.push(`origin (${originCode})`)
    if (!locationsByCode[destinationCode]) missingLocations.push(`destination (${destinationCode})`)

    if (missingLocations.length) {
      const missingLabel = missingLocations.join(' and ')
      throw new Error(`Could not find ${missingLabel} location${missingLocations.length > 1 ? 's' : ''}`)
    }

    const safeLines = Array.isArray(lines) ? lines : []

    if (safeLines.length === 0) {
      const err = new Error(`No shipment lines provided for shipmentId: ${shipmentId}`)
      err.status = 400
      throw err
    }

    const counts = {}
    const lineProblems = []

    safeLines.forEach((line, index) => {
      const { valid, issues } = validateShipmentLine(line)

      if (!valid) {
        const productId = line && line.productId
        const label = productId ? `line[${index}] (productId: ${productId})` : `line[${index}]`
        lineProblems.push(`${label}: ${issues.join(', ')}`)
        return
      }

      const { productId, batchNumber, manufacturer, quantity } = line
      const key = batchNumber
        ? `product:${productId}:manufacturer:${manufacturer || 'unknown'}:batchNo:${batchNumber}`
        : `product:${productId}:manufacturer:unknown:batchNo:UNKNOWN`
      if (!counts[key]) counts[key] = { quantity: 0 }
      counts[key].quantity += quantity
    })

    if (Object.keys(counts).length === 0) {
      const err = new Error(`No valid shipment lines for shipmentId: ${shipmentId}. ${lineProblems.join('; ')}`)
      err.status = 400
      throw err
    }

    // Normalize date string to YYYY-MM-DD format first (handles DD-MM-YYYY, DD/MM/YYYY, etc.)
    // This ensures dates from RMS SAP in various formats are properly converted
    let dateStr
    if (expectedDeliveryDate) {
      // If it's a string, normalize it to YYYY-MM-DD format first
      const normalizedDateStr = typeof expectedDeliveryDate === 'string'
        ? fsShipmentDateParser(expectedDeliveryDate)
        : expectedDeliveryDate

      // Convert to Date object and normalize to local date without time
      const deliveryDate = new Date(normalizedDateStr)
      // Check if date is valid
      if (isNaN(deliveryDate.getTime())) {
        // If parsing failed, try using the normalized string directly if it's already YYYY-MM-DD
        if (typeof normalizedDateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(normalizedDateStr)) {
          dateStr = normalizedDateStr
        } else {
          throw new Error(`Invalid expectedDeliveryDate format: ${expectedDeliveryDate}`)
        }
      } else {
        // Normalize to local date without time; ensure stable YYYY-MM-DD regardless of TZ
        dateStr = new Date(deliveryDate.getTime() - deliveryDate.getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 10)
      }
    } else {
      // Default to current date
      const now = new Date()
      dateStr = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 10)
    }

    await this.batchHelpers.fromRmsLines(safeLines)

    const [snapshotWithPrices] = await this.mainApi.shipment.decorateShipmentSnapshotsWithResolvedPrices([{
      status: SHIPMENT_STATUS.PACKED,
      counts,
      origin: { id: locationsByCode[originCode]._id },
      destination: { id: locationsByCode[destinationCode]._id },
      date: dateStr,
      externalShipmentId: shipmentId,
      orderId,
      programId,
      funderId
    }])

    // Return the snapshot directly. The previous `shipment.findById` lookup was
    // racing CouchDB replication in production multi-node setups and intermittently
    // failing with "Shipments-find: unexpected result, found: 0, expected 1" — see
    // fielded/field-supply#23314.
    const snapshot = await this.mainApi.shipment.createSnapshot(snapshotWithPrices)

    this.logOperationSuccess('shipmentsPost', snapshot, {
      shipmentId,
      orderId,
      linesCount: safeLines.length
    })

    return snapshot
  }

  async getBranches (options = {}) {
    this.logOperationStart('getBranches', { options })

    try {
      const result = await this.rmsAdapter.getBranches(options)

      this.logOperationSuccess('getBranches', result, {
        total: result.total,
        page: result.page,
        size: result.size
      })

      return result
    } catch (error) {
      this.logOperationError('getBranches', error, { options })
      throw error
    }
  }

  async getStockData (options = {}) {
    this.logOperationStart('getStockData', { options })

    try {
      const result = await this.rmsAdapter.getStockData(options)

      this.logOperationSuccess('getStockData', result, {
        total: result.total,
        page: result.page,
        size: result.size,
        timestamp: result.timestamp
      })

      return result
    } catch (error) {
      this.logOperationError('getStockData', error, { options })
      throw error
    }
  }

  async getStockDataBatch (options = {}) {
    this.logOperationStart('getStockDataBatch', { options })

    try {
      const result = await this.rmsAdapter.getStockDataBatch(options)

      this.logOperationSuccess('getStockDataBatch', result, {
        totalItems: result.items.length,
        batchInfo: result.batchInfo,
        timestamp: result.timestamp
      })

      return result
    } catch (error) {
      this.logOperationError('getStockDataBatch', error, { options })
      throw error
    }
  }

  async getOutOfStockData (options = {}) {
    this.logOperationStart('getOutOfStockData', { options })

    try {
      const result = await this.rmsAdapter.getOutOfStockData(options)

      this.logOperationSuccess('getOutOfStockData', result, {
        totalItems: result.items.length,
        batchInfo: result.batchInfo,
        timestamp: result.timestamp
      })

      return result
    } catch (error) {
      this.logOperationError('getOutOfStockData', error, { options })
      throw error
    }
  }

  async getProductPricesData () {
    this.logOperationStart('getProductPricesData')

    try {
      const result = await this.rmsAdapter.getProductPricesData()

      this.logOperationSuccess('getProductPricesData', result, {
        totalItems: Array.isArray(result.items) ? result.items.length : 0,
        timestamp: result.timestamp
      })

      return result
    } catch (error) {
      this.logOperationError('getProductPricesData', error)
      throw error
    }
  }

  async getProductPricesDataBatch () {
    this.logOperationStart('getProductPricesDataBatch')

    try {
      const result = await this.rmsAdapter.getProductPricesDataBatch()

      this.logOperationSuccess('getProductPricesDataBatch', result, {
        totalItems: Array.isArray(result.items) ? result.items.length : 0,
        batchInfo: result.batchInfo,
        timestamp: result.timestamp
      })

      return result
    } catch (error) {
      this.logOperationError('getProductPricesDataBatch', error)
      throw error
    }
  }

  async getPurchaseOrdersDataBatch (options = {}) {
    this.logOperationStart('getPurchaseOrdersDataBatch', { options })

    try {
      const result = await this.rmsAdapter.getPurchaseOrdersDataBatch(options)

      this.logOperationSuccess('getPurchaseOrdersDataBatch', result, {
        totalItems: result.items.length,
        batchInfo: result.batchInfo,
        timestamp: result.timestamp
      })

      return result
    } catch (error) {
      this.logOperationError('getPurchaseOrdersDataBatch', error, { options })
      throw error
    }
  }

  async getPurchaseOrdersDetailBatch (poNumbers = []) {
    this.logOperationStart('getPurchaseOrdersDetailBatch', { count: poNumbers.length })

    try {
      const result = await this.rmsAdapter.getPurchaseOrdersDetailBatch(poNumbers)

      this.logOperationSuccess('getPurchaseOrdersDetailBatch', result, {
        totalItems: result.items.length
      })

      return result
    } catch (error) {
      this.logOperationError('getPurchaseOrdersDetailBatch', error, { count: poNumbers.length })
      throw error
    }
  }

  async getSalesOrdersDataBatch (options = {}) {
    this.logOperationStart('getSalesOrdersDataBatch', { options })

    try {
      const result = await this.rmsAdapter.getSalesOrdersDataBatch(options)

      this.logOperationSuccess('getSalesOrdersDataBatch', result, {
        totalItems: result.items.length,
        batchInfo: result.batchInfo,
        timestamp: result.timestamp
      })

      return result
    } catch (error) {
      this.logOperationError('getSalesOrdersDataBatch', error, { options })
      throw error
    }
  }
}
class RmsInternalApi extends ProxifiedEntityApi {
  constructor (mainApi, pgConnection, agaveAdapter, logger, user) {
    const username = user.name
    const adapter = new StockTransformAdapter(mainApi, pgConnection, logger, username)
    super(
      'rmsInternal',
      ['list', 'listStockEvents'],
      !pgConnection,
      adapter,
      agaveAdapter
    )

    this.adapter = adapter
  }
  async listStockEvents ({organisationId, branchCode = null, limit = 25, offset = 0}) {
    return this.adapter.stockEvents({ organisationId, branchCode, limit, offset })
  }
  async getWarehouseStocks ({ organisationId, date, branchCode }) {
    return this.adapter.getWarehouseStocks({ organisationId, date, branchCode })
  }
  async listWarehouseStock ({ organisationId, date, branchCode = null }) {
    return this.agaveAdapter.getBlob('rmsInternal/warehouse-stock', { organisationId, date, branchCode })
  }
}

module.exports = RmsApi
module.exports.RmsInternalApi = RmsInternalApi
